import { Link } from 'react-router-dom';
import { useAllFindings } from '../../api/queries';
import type { Finding } from '../../api/types';
import { FindingCard } from '../../components/FindingCard';
import { QueryState } from '../../components/States';
import { plural } from '../../lib/format';
import { StewardNav } from './StewardNav';

function Group({ title, description, findings }: { title: string; description: string; findings: Finding[] }) {
  return (
    <section className="queue-group" aria-labelledby={`q-${title}`}>
      <h2 id={`q-${title}`} className="section-title">
        {title} <span className="evidence-group__count">{findings.length}</span>
      </h2>
      <p className="muted">{description}</p>
      {findings.length === 0 ? (
        <p className="muted">Nothing here.</p>
      ) : (
        <ul className="card-list">
          {findings.map((finding) => (
            <li key={finding.id}>
              <FindingCard finding={finding} />
              <Link className="text-link" to={`/findings/${encodeURIComponent(finding.id)}#review`}>
                Review version {finding.version}
                <span className="visually-hidden"> of “{finding.title}”</span> →
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function ReviewQueue() {
  const findings = useAllFindings({});
  return (
    <main id="main" tabIndex={-1} className="page">
      <header className="page__header">
        <p className="eyebrow">Stewardship</p>
        <h1 className="page__title">Review queue</h1>
        <p className="lede">
          Decide on findings against their evidence. Every decision needs a reason and is made against a specific
          version, so a concurrent decision is refused rather than overwritten.
        </p>
      </header>
      <StewardNav />
      <QueryState query={findings} label="findings">
        {(page) => {
          const flagged = page.items.filter((finding) => finding.needsReview && finding.status === 'reviewed');
          const proposed = page.items.filter((finding) => finding.status === 'proposed');
          const contested = page.items.filter((finding) => finding.status === 'contested');
          return (
            <>
              <p className="result-count">
                {plural(flagged.length + proposed.length + contested.length, 'finding')} waiting across {plural(page.items.length, 'visible finding')}.
              </p>
              <Group
                title="Reviewed, but flagged"
                description="A correction, challenge, proposal or changed source arrived after review. Exports that include these are stale."
                findings={flagged}
              />
              <Group title="Proposed" description="Drafts that no steward has reviewed yet." findings={proposed} />
              <Group title="Contested" description="Findings with a recorded, unresolved disagreement." findings={contested} />
            </>
          );
        }}
      </QueryState>
    </main>
  );
}
